/**
 * AuditGridPage  (route: /audit)
 *
 * Tutor-camera-only audit recording for an lms-backend audit group: up to 15
 * classes tiled on one page, no audio. Each tile joins its class room with
 * autoSubscribe off and subscribes to the tutor's camera only.
 *
 * Membership is dynamic: every POLL_MS the page heartbeats
 * /api/v2/recording-egress/audit-groups/:groupId/heartbeat, reporting finished
 * classes and getting back the live members (classId + room token). New members
 * take the first free tile; dropped ones free theirs. When the backend ends the
 * group it also stops the egress; END_RECORDING here is the fallback.
 *
 * URL params: groupId, groupToken, wsUrl, maxWaitMin (tutor never joined; default 30).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LiveKitRoom,
  VideoTrack,
  useConnectionState,
  useRemoteParticipants,
  useTracks,
} from '@livekit/components-react';
import { ConnectionState, Track, type Participant, type RemoteTrackPublication } from 'livekit-client';
import { datadogLogs } from '@datadog/browser-logs';

import apiService from 'hooks/apiService';
import { getPresetFromMetadata, isInClassBotIdentity } from 'adapters/LiveKitMeetingAdapter';
import { PRESET_TYPE } from 'configs/meeting';
import { AUTO_END_RECORDING_MINUTES } from 'configs/config';
import TimeComponent from 'components/TimeComponent';
import LiveKitReconnect from 'hooks/useLiveKitReconnect';

const TILES = 15;
const POLL_MS = 5000;

type Member = { classId: string; token: string; rev?: number };

// The backend bumps `rev` when the class's start is called again (an app could
// not see this recorder), so a new key remounts the tile with its fresh token.
const tileKey = (m: Member) => `${m.classId}:${m.rev ?? 0}`;
type TileStatus = { tutorPresent: boolean; connected: boolean; gaveUp: boolean; seen: boolean; lastAt: number; addedAt: number };

const isTutor = (p: Participant) =>
  !isInClassBotIdentity(p.identity) && getPresetFromMetadata(p.metadata) === PRESET_TYPE.TUTOR;

const log = (message: string, extra: Record<string, unknown> = {}) =>
  datadogLogs.logger.info(message, { label: 'audit-recording', source: 'recording-app', ...extra });

const TutorCamera = ({ classId, onStatus }: { classId: string; onStatus: (s: Partial<TileStatus>) => void }) => {
  const tutorPresent = useRemoteParticipants().some(isTutor);
  const connected = useConnectionState() === ConnectionState.Connected;
  const camera = useTracks([Track.Source.Camera], { onlySubscribed: false }).find((t) => isTutor(t.participant));
  const publication = camera?.publication as RemoteTrackPublication | undefined;

  useEffect(() => onStatus({ tutorPresent, connected }), [tutorPresent, connected, onStatus]);

  useEffect(() => {
    if (publication && !publication.isSubscribed) publication.setSubscribed(true);
  }, [publication]);

  return (
    <div className='relative h-full w-full overflow-hidden bg-black'>
      {camera?.publication?.track ? (
        <VideoTrack trackRef={camera} className='h-full w-full object-contain' />
      ) : (
        <div className='flex h-full items-center justify-center text-sm text-gray-500'>
          {tutorPresent ? 'Tutor camera off' : 'Tutor not in class'}
        </div>
      )}
      <div className='absolute bottom-0 left-0 bg-black/70 px-2 py-0.5 text-xs text-white'>
        Class {classId}
        {camera ? ` · ${camera.participant.name || camera.participant.identity}` : ''}
      </div>
    </div>
  );
};

/** Keep each member in its tile; new members take the first free one. */
const placeMembers = (prev: (Member | null)[], members: Member[]): (Member | null)[] => {
  const byId = new Map(members.map((m) => [m.classId, m]));
  // Existing tiles keep their original token (same identity, no reconnect).
  const next = prev.map((m) => {
    const cur = m && byId.get(m.classId);
    if (!cur) return null;
    return (cur.rev ?? 0) === (m!.rev ?? 0) ? m : cur;
  });
  const placed = new Set(next.filter(Boolean).map((m) => m!.classId));
  for (const m of members) {
    if (placed.has(m.classId)) continue;
    const free = next.indexOf(null);
    if (free === -1) break;
    next[free] = m;
  }
  return next.every((m, i) => m === prev[i]) ? prev : next;
};

const AuditGridPage = () => {
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('groupId');
  const groupToken = searchParams.get('groupToken');
  const wsUrl = searchParams.get('wsUrl');
  const maxWaitMs = (Number(searchParams.get('maxWaitMin')) || 30) * 60000;
  const emptyMs = AUTO_END_RECORDING_MINUTES * 60000;

  const [tiles, setTiles] = useState<(Member | null)[]>(() => Array(TILES).fill(null));
  const [ended, setEnded] = useState(false);
  // Read by the poll only, so tile updates never re-render the grid.
  const statusRef = useRef<Record<string, TileStatus>>({});
  const tilesRef = useRef(tiles);
  useEffect(() => {
    tilesRef.current = tiles;
    // A class that rejoins later starts its countdown fresh.
    const live = new Set(tiles.filter(Boolean).map((m) => tileKey(m!)));
    for (const id of Object.keys(statusRef.current)) if (!live.has(id)) delete statusRef.current[id];
  }, [tiles]);

  const report = useCallback((key: string, s: Partial<TileStatus>) => {
    const now = Date.now();
    const prev = statusRef.current[key] ?? { tutorPresent: false, connected: false, gaveUp: false, seen: false, lastAt: now, addedAt: now };
    statusRef.current[key] = { ...prev, ...s };
  }, []);
  const reporters = useMemo(
    () => Object.fromEntries(tiles.filter(Boolean).map((m) => [tileKey(m!), (s: Partial<TileStatus>) => report(tileKey(m!), s)])),
    [tiles, report],
  );

  /** A tile is done once its tutor left for emptyMs, never came, or the room is lost. */
  const doneClassIds = useCallback((now: number) => tilesRef.current.filter(Boolean).filter((m) => {
    const s = statusRef.current[tileKey(m!)];
    if (!s) return false;
    if (s.gaveUp) return true;
    // Offline is "can't see", not "empty": hold the countdown like useAutoEndRecording.
    if (s.tutorPresent || (!s.connected && s.seen)) {
      s.seen = s.seen || s.tutorPresent;
      s.lastAt = now;
      return false;
    }
    return s.seen ? now - s.lastAt >= emptyMs : now - s.addedAt >= maxWaitMs;
  }).map((m) => m!.classId), [emptyMs, maxWaitMs]);

  useEffect(() => {
    if (!groupId || !groupToken || ended) return;
    let cancelled = false;
    const beat = async () => {
      const done = doneClassIds(Date.now());
      try {
        const { data } = await apiService.post(
          `/api/v2/recording-egress/audit-groups/${groupId}/heartbeat`,
          { done },
          { headers: { Authorization: `Bearer ${groupToken}` } },
        );
        if (cancelled) return;
        const { end, members } = data?.data ?? {};
        if (end) {
          log('audit group ended; ending recording', { groupId });
          // LiveKit web egress stops on this console line.
          console.log('END_RECORDING');
          setEnded(true);
          return;
        }
        setTiles((prev) => placeMembers(prev, (members ?? []).map((m: Member) => ({ ...m, classId: String(m.classId) }))));
      } catch (error) {
        // Keep recording what we have; the next beat retries.
        datadogLogs.logger.warn('audit heartbeat failed', { label: 'audit-recording', groupId, error: (error as Error)?.message });
      }
    };
    beat();
    const id = setInterval(beat, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [groupId, groupToken, ended, doneClassIds]);

  if (!groupId || !groupToken || !wsUrl) {
    return (
      <div className='flex h-screen items-center justify-center bg-black text-red-500'>
        Requires groupId, groupToken and wsUrl
      </div>
    );
  }

  // 16:9 page + 16:9 tiles: 15 tiles at 1920x1080 = 4x4 of 480x270, fixed so tiles never move.
  return (
    <div className='relative h-screen w-screen bg-black'>
      <div className='absolute right-1 top-1 z-10'>
        <TimeComponent />
      </div>
      <div className='grid h-full w-full grid-cols-4 grid-rows-4 gap-px'>
        {tiles.map((member, i) =>
          member && !ended ? (
            <LiveKitRoom
              key={tileKey(member)}
              className='min-h-0 min-w-0'
              serverUrl={wsUrl}
              token={member.token}
              connect
              audio={false}
              video={false}
              options={{ adaptiveStream: true }}
              connectOptions={{ autoSubscribe: false }}
              onError={(error) =>
                datadogLogs.logger.error('audit grid room error', { label: 'audit-recording', groupId, classId: member.classId, error: error.message })
              }
            >
              <TutorCamera classId={member.classId} onStatus={reporters[tileKey(member)]} />
              <LiveKitReconnect
                serverUrl={wsUrl}
                token={member.token}
                logContext={{ page: 'audit', groupId, classId: member.classId }}
                onGiveUp={() => report(tileKey(member), { gaveUp: true })}
              />
            </LiveKitRoom>
          ) : (
            <div key={`empty-${i}`} className='bg-black' />
          ),
        )}
      </div>
    </div>
  );
};

export default AuditGridPage;
